import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui/toast";
import type { AppNotification } from "@/features/engagement/types/notification";
import { getSocket } from "@/lib/socket";
import { useAppSelector } from "@/store/hooks";

/**
 * Mounted once, app-wide (`SocketConnectionManager`) — not per-page, since
 * a notification ("Someone messaged you", "Price dropped on an item you
 * wishlisted") is meaningful no matter which page the user is currently
 * on, matching docs/notes/sprint1.3.md's Notification Architecture Flow:
 * "If user online → Socket.IO emits notification."
 *
 * Depends on `socket.status`, not just `queryClient` (which never
 * changes) — `getSocket()` returns `null` until `useSocketConnection`'s own
 * effect actually calls `connectSocket`, and that only happens once
 * `isAuthenticated` flips true. Without this dependency, mounting before
 * that flip means `getSocket()` returns `null` once, the effect never
 * reruns, and this listener never attaches for the rest of the session.
 */
export function useNotificationSocketEvents(): void {
  const queryClient = useQueryClient();
  const socketStatus = useAppSelector((state) => state.socket.status);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    function handleNewNotification(notification: AppNotification) {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      showToast.info(notification.title);
    }

    socket.on("new_notification", handleNewNotification);
    return () => {
      socket.off("new_notification", handleNewNotification);
    };
  }, [queryClient, socketStatus]);
}
